import adler32 from 'adler-32'
import axios from 'axios'
import cronstrue from 'cronstrue'
import semverCompare from 'semver/functions/compare'
import generatePassword from 'password-generator'
import pluralize from 'pluralize'
import sample from 'lodash/sample'
import d2c from '@/d2c'
import logger from '@/logger'
import objects from '@/objects'
import store from '@/store'

const utils = {
  adler32(str = '') {
    return adler32.str(str).toString(36)
  },
  generateArray(maxLength = 5, minLength = 1) {
    const array = []
    const length = utils.generateNumber(maxLength, minLength)
    for (let i = 0; i < length; i++) {
      array.push(null)
    }
    return array
  },
  generateBoolean(chance = 0.5) {
    return Math.random() <= chance
  },
  generateColor() {
    return '#' + utils.generateString(6, '0123456789ABCDEF')
  },
  generateCountry() {
    return sample(d2c.countries)
  },
  generateDate(period = 30) {
    return new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000 * period).toISOString()
  },
  generateDomain() {
    const name = utils.generateName()
    const zone = sample(['com', 'io', 'net', 'org', 'ru'])
    return `${name}.${zone}`
  },
  generateEmail() {
    const name = utils.generateName()
    const domain = utils.generateDomain()
    return `${name}@${domain}`
  },
  generateIcon() {
    return sample(d2c.icons)
  },
  generateId(length = 24) {
    let id = ''
    for (var i = 0; i < length; i++) {
      id += (Math.random() * 16 | 0).toString(16)
    }
    return id
  },
  generateIP() {
    return `${utils.generateNumber(1, 255)}.${utils.generateNumber(1, 255)}.${utils.generateNumber(1, 255)}.${utils.generateNumber(1, 255)}`
  },
  generateImage(props) {
    const backgroundColor = utils.generateColor().substr(1)
    const color = utils.invertColor(backgroundColor, true).substr(1)
    const text = utils.generateString(1).toUpperCase()
    const options = {width: 80, height: 80, backgroundColor, color, text, ...props}
    return `http://via.placeholder.com/${options.width}x${options.height}/${options.backgroundColor}/${options.color}?text=${options.text}`
  },
  generateLogo(target = null) {
    switch (target) {
      case 'host':
        target = 'provider'
        break
      case 'container':
        target = 'service'
        break
      case null:
        target = sample(['provider', 'service'])
        break
    }
    return sample(objects[`_${target}s`])._logo
  },
  generateName() {
    return sample(d2c.names)
  },
  generateNumber(max = 1, min = 0) {
    const from = Math.min(max, min)
    const to = Math.max(max, min)
    return from + Math.round(Math.random() * (to - from))
  },
  generatePassword(length = 8, readable = false, pattern = /([a-zA-Z\d])/) {
    let password = ''
    length = Math.max(length, 8)
    while (!utils.isStrongPassword(password)) {
      password = generatePassword(length, readable, pattern)
    }
    return password
  },
  generatePublicKey() {
    const string = utils.generateString(711, 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/')
    const email = utils.generateEmail()
    return `ssh-rsa AAA${string}== ${email}`
  },
  generateString(length = 1, characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789') {
    let result = ''
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length))
    }
    return result
  },
  generateText(maxWords = 20, minWords = 10) {
    return utils.generateArray(maxWords, minWords).map(() => utils.generateIcon()).join(' ')
  },
  generateProject(props = {}) {
    return objects.Project.create({
      created: utils.generateDate(),
      id: utils.generateId(),
      inherits: true,
      modified: utils.generateDate(),
      name: utils.generateName(),
      owner: store.getters.account.id,
      services: [],
      shortId: utils.generateString(10),
      ...props,
    })
  },
  generateProvider(props = {}) {
    const providerConstructors = objects._providers
    const purposeConstructors = providerConstructors.filter(provider => !props.purpose || props.purpose === provider.purposeName)
    const typeConstructors = purposeConstructors.filter(provider => !props.type || props.type === provider.providerType)
    const constructor = sample(typeConstructors)
    return constructor.create({
      created: utils.generateDate(),
      expired: false,
      id: utils.generateId(),
      modified: utils.generateDate(),
      name: utils.generateName(),
      owner: store.getters.account.id,
      type: constructor.providerType,
      ...props,
    })
  },
  generateNotification(props = {}) {
    const types = ['warning', 'error', 'info', 'success']
    return {
      entity: sample(store.getters.entities),
      type: sample(types),
      text: 'Description',
      ...props,
    }
  },
  getDiff(object1, object2, path = '', diff = {}) {
    if (typeof object1 === 'object' && typeof object2 === 'object') {
      for (const key in object2) {
        const isChangedArray = object1[key] instanceof Array && object1[key].length !== object2[key].length
        const newPath = path ? path + '.' + key : key
        if (typeof object2[key] === 'object' && !isChangedArray) {
          diff = this.getDiff(object1[key], object2[key], newPath, diff)
        } else if (object1[key] !== object2[key]) {
          diff[newPath] = [JSON.stringify(object1[key]), JSON.stringify(object2[key])]
        }
      }
    } else {
      diff[path] = [JSON.stringify(object1), JSON.stringify(object2)]
    }
    return diff
  },
  getPercentColors(percent) {
    if (typeof percent === 'number') {
      if (percent >= 0 && percent <= 50) return ['#0c3', '#beb']
      if (percent > 50 && percent <= 70) return ['#fc0', '#dda']
      if (percent > 70 && percent <= 90) return ['#f60', '#db8']
      if (percent > 90) return ['#c33', '#daa']
    }
    return ['#666', '#999']
  },
  getStatusColor(entity) {
    if (entity._wasSuccessful) {
      return 'teal'
    }
    if (entity._wasFailed) {
      return 'red'
    }
    if (entity._inProgress) {
      return 'blue'
    }
    if (entity.target === 'container' && entity._isRestarting) {
      return 'orange'
    }
    if (entity.target === 'service' && !entity._isDeployed) {
      return 'orange'
    }
    if (entity.target === 'host' && !entity._isCreated) {
      return 'orange'
    }
    if (entity.target === 'host' && !entity._isSetup) {
      return 'grey'
    }
    if (entity._isOnline) {
      return 'green'
    }
    if (entity._isOffline) {
      return 'grey'
    }
    return 'grey'
  },
  getStatusIcon(entity) {
    if (entity._wasSuccessful) {
      return 'check'
    }
    if (entity._wasFailed) {
      return 'warning sign'
    }
    if (entity._inProgress) {
      return 'loading cog'
    }
    if (entity.constructor.statuses.NOT_DEPLOYED && !entity._isDeployed) {
      return 'download'
    }
    if (entity.constructor.statuses.NOT_CREATED && !entity._isCreated) {
      return 'cloud upload alternate'
    }
    return 'power'
  },
  getPublicKey() {
    const key = {
      url: `${store.getters.config.s3PublicUrl}/keys/${store.getters.account.id}/id_rsa.pub`,
      value: '',
      error: null,
    }
    return utils.request({url: key.url})
      .then(publicKey => {
        key.value = publicKey
        return key
      }).catch(error => {
        key.error = error
        return key
      })
  },
  addScript(url) {
    return new Promise(resolve => {
      const script = document.createElement('script')
      script.src = url
      script.addEventListener('load', resolve)
      document.head.appendChild(script)
    })
  },
  isArchiveUrl(url) {
    const regex = /(\.tar\.bz2|\.tar\.gz|\.tar|\.zip)$/
    return regex.test(url)
  },
  isGitUrl(url) {
    const regex = /(?:git|ssh|https?|git@[-\w.]+):(\/\/)?(.*?)(\.git)(\/?|#[-\d\w._]+?)$/
    return regex.test(url)
  },
  isRepoUrl(url) {
    return this.matchGithub(url) || this.matchBitbucket(url) || this.matchGitlab(url)
  },
  isStrongPassword(password) {
    const enoughLength = password.length >= 8
    const hasUppercase = password.match(/[A-Z]/)
    const hasLowercase = password.match(/[a-z]/)
    const hasDigit = password.match(/\d/)
    return enoughLength && hasUppercase && hasLowercase && hasDigit
  },
  matchGithub(url) {
    return url.match(/git@github\.com:([\w-_]+)\/([\w-_]+)\.git/) || url.match(/https:\/\/github\.com\/([\w-_]+)\/([\w-_]+)\/?((?:tree|commit)\/([\w-_]+))?/i) // [url, team, name, path, branch]
  },
  matchBitbucket(url) {
    return url.match(/git@bitbucket\.org:([\w-_]+)\/([\w-_]+)\.git/) || url.match(/https:\/\/bitbucket\.org\/([\w-_]+)\/([\w-_]+)\/?((?:src|commits)\/([\w-_]+))?/i) // [url, team, name, path, branch]
  },
  matchGitlab(url) {
    return url.match(/git@gitlab\.com:([\w-_]+)\/([\w-_]+)\.git/) || url.match(/https:\/\/gitlab\.com\/([\w-_]+)\/([\w-_]+)\/?((?:tree|commit)\/([\w-_]+))?/i) // [url, team, name, path, branch]
  },
  getRepoArchive(url) {
    const matchGithub = this.matchGithub(url)
    const matchBibucket = this.matchBitbucket(url)
    const matchGitlab = this.matchGitlab(url)
    if (matchGithub) {
      return `https://github.com/${matchGithub[1]}/${matchGithub[2]}/archive/${matchGithub[4] || 'master'}.zip`
    }
    if (matchBibucket) {
      return `https://bitbucket.org/${matchBibucket[1]}/${matchBibucket[2]}/get/${matchBibucket[4] || 'master'}.zip`
    }
    if (matchGitlab) {
      return `https://gitlab.com/${matchGitlab[1]}/${matchGitlab[2]}/-/archive/${matchGitlab[4] || 'master'}/myaccount-dev-${matchGitlab[4] || 'master'}.zip`
    }
    return null
  },
  invertColor(hex, bw) {
    if (hex.startsWith('#')) {
      hex = hex.slice(1)
    }
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
    }
    if (hex.length !== 6) {
      throw new Error('Invalid HEX color.')
    }
    let r = parseInt(hex.slice(0, 2), 16)
    let g = parseInt(hex.slice(2, 4), 16)
    let b = parseInt(hex.slice(4, 6), 16)
    if (bw) {
      return (r * 0.299 + g * 0.587 + b * 0.114) > 186 ? '#000000' : '#FFFFFF'
    }
    r = (255 - r).toString(16)
    g = (255 - g).toString(16)
    b = (255 - b).toString(16)
    return '#' + utils.padZero(r) + utils.padZero(g) + utils.padZero(b)
  },
  padZero(str = '', length = 2) {
    if (str.length > length) {
      return str
    }
    return (utils.generateString(length, '0') + str).slice(-length)
  },
  pluralize(...args) {
    return pluralize(...args)
  },
  prettyBytes(bytes, decimals) {
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    const number = bytes / Math.pow(1024, i)
    const dm = decimals || (number > 99 ? 0 : 1)
    return bytes ? number.toFixed(dm) + ' ' + sizes[i] : '0 B'
  },
  prettyCron(value) {
    let result = null
    try {
      result = cronstrue.toString(value)
    } catch (error) {
      logger.warning(`Invalid cron schedule: "${value}". ${error}`)
    }
    return result
  },
  reachGoal(goal, params) {
    store.dispatch('goal', {goal, params})
  },
  request(options = {}) {
    let time = Date.now()
    options = {method: 'GET', ...options}
    logger.info(`🔀 ${options.method} ${options.url}`, {options})
    return axios(options)
      .finally(() => {
        time = Date.now() - time
      })
      .then(response => {
        if (response.data.error) {
          throw new Error(response.data.error)
        }
        logger.success(`✅ ${options.method} ${options.url} ⌛ ${time} ms`, {result: response.data})
        return response.data
      })
      .catch(response => {
        if (response instanceof axios.Cancel) {
          logger.warning(`🚧 ${options.method} ${options.url} The request has been canceled! ⌛ ${time} ms`)
          return
        }
        const error = response.message || 'Unknown Error'
        logger.fail(`⛔ ${options.method} ${options.url} ${error} ⌛ ${time} ms`)
        throw new Error(error)
      })
  },
  semverCompare(...args) {
    return semverCompare(...args)
  },
  scrollTo(element, to, duration = 100) {
    if (duration <= 0) return;
    var difference = to - element.scrollTop;
    var perTick = difference / duration * 10;
    setTimeout(() => {
      element.scrollTop = element.scrollTop + perTick;
      if (element.scrollTop === to) return;
      utils.scrollTo(element, to, duration - 10);
    }, 10);
  },
  checkUpdates() {
    utils.request({url: '/version.json'}).then(data => {
      if (semverCompare(data.version, store.state.version) > 0) {
        d2c.logger.notify({
          id: 'update',
          type: 'warning',
          header: 'Update',
          icon: 'arrow alternate circle up',
          text: 'An update of D2C Panel is available. Reload the page to apply fixes and improvements.',
          buttons: [
            { text: 'Reload page', class: 'green', action() { document.location.reload(true) } },
            { text: 'Later', class: 'basic green' }
          ]
        })
      }
    })
  },
}

export default utils
